import React from "react"
import styled from "styled-components"
import {
	Flex,
	Text,
	Icon,
	Drawer,
	StackNavigation,
	StackNavigationContext,
	StackNavigationView,
	colors,
} from "@capioweb/ui-components"
import { PrimaryMenuProps } from "../types"
import { UIContext } from "../../../context"
import { MenuItem } from "../../../@types/CapioWeb/menu"
import { Z_INDEX } from "../../../constants"
import { sendNavigationDataLayerPush } from "../../../utils/piwikPro"

const LevelOneLink = styled(Text.Link)`
	&:hover {
		color: ${colors.navy};
	}
`

interface MenuLevelProps {
	title: string
	menu: MenuItem[]
	toggleMenu: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void
}

const MenuLevelOne: React.FC<MenuLevelProps> = (props) => {
	const level = 1

	return (
		<Flex w={1} flexDirection="column" mb={4}>
			{props.menu.map((item, i) =>
				item.children && item.children.length > 0 ? (
					<StackNavigation.Nav
						to={`${item?.children[0]?.id ?? "#"}l${level}`}
						key={i}
					>
						<Flex
							w={1}
							justifyContent="space-between"
							alignItems="center"
							py={1.25}
						>
							<Text fontSize={2.75} fontWeight="bold" mb={0}>
								{item.name}
							</Text>
							<Icon icon="arrow-right" mb={-0.25} mr={-0.5} />
						</Flex>
					</StackNavigation.Nav>
				) : (
					item.url && (
						<LevelOneLink
							to={item?.url ?? "#"}
							onClick={(e) => {
								props.toggleMenu(e)
								sendNavigationDataLayerPush("main_menu", item.name)
							}}
							fontSize={2.75}
							fontWeight="bold"
							py={1.5}
							mb={0}
							key={i}
						>
							{item.name}
						</LevelOneLink>
					)
				)
			)}
		</Flex>
	)
}

const MenuLevelTwo: React.FC<MenuLevelProps> = (props) => {
	const level = 2

	return (
		<Flex w={1} flexDirection="column" borderTop>
			{props.menu.map((item, i) =>
				item.children && item.children.length > 0 ? (
					<StackNavigation.Nav
						to={`${item?.children[0]?.id ?? "#"}l${level}`}
						key={i}
					>
						<Flex
							w={1}
							borderBottom
							justifyContent="space-between"
							alignItems="center"
							py={1.5}
						>
							<Text fontSize={2} fontWeight="bold" mb={0}>
								{item.name}
							</Text>
							{item.children && (
								<Icon icon="arrow-right" mb={-0.25} mr={-0.5} />
							)}
						</Flex>
					</StackNavigation.Nav>
				) : (
					<Flex key={i} w={1} py={1.5} borderBottom>
						<Text.Link
							to={item?.url ?? "#"}
							onClick={(e) => {
								props.toggleMenu(e)
								sendNavigationDataLayerPush("main_menu", item.name)
							}}
							fontSize={2}
							fontWeight="bold"
							mb={0}
						>
							{item.name}
						</Text.Link>
					</Flex>
				)
			)}
		</Flex>
	)
}

const MenuLevelThree: React.FC<MenuLevelProps> = (props) => {
	return (
		<Flex w={1} flexDirection="column" my={1}>
			{props.menu.map((item, i) => (
				<Text.Link
					to={item?.url ?? "#"}
					onClick={(e) => {
						props.toggleMenu(e)
						sendNavigationDataLayerPush("main_menu", item.name)
					}}
					fontSize={1.75}
					lineHeight={4}
					mb={2}
					key={i}
				>
					{item.name}
				</Text.Link>
			))}
		</Flex>
	)
}

const mapMenu = (
	toggleMenu: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void,
	menu: MenuItem[],
	secondaryMenu: MenuItem[] = [],
	previousMenu?: MenuItem,
	level = 0
): StackNavigationView[] => {
	const view = {
		id: `${menu[0].id}l${level}`,
		title: previousMenu?.name,
		component: () => (
			<Flex as="nav" w={1} flexDirection="column" px={3} pt={5}>
				{level === 0 ? (
					<>
						<MenuLevelOne
							title={previousMenu?.name}
							menu={menu}
							toggleMenu={toggleMenu}
						/>
						<MenuLevelTwo
							title={previousMenu?.name}
							menu={secondaryMenu}
							toggleMenu={toggleMenu}
						/>
					</>
				) : level === 1 ? (
					<MenuLevelTwo
						title={previousMenu?.name}
						menu={menu}
						toggleMenu={toggleMenu}
					/>
				) : (
					<MenuLevelThree
						title={previousMenu?.name}
						menu={menu}
						toggleMenu={toggleMenu}
					/>
				)}
			</Flex>
		),
	}

	return [
		{ ...view },
		...menu.reduce<StackNavigationView[]>(
			(acc, item) =>
				item.children && item.children.length > 0
					? [
							...acc,
							...mapMenu(
								toggleMenu,
								[...item.children],
								[],
								item,
								item.level + 1
							),
					  ]
					: acc,
			[]
		),
		...secondaryMenu.reduce<StackNavigationView[]>(
			(acc, item) =>
				item.children && item.children.length > 0
					? [...acc, ...mapMenu(toggleMenu, [...item.children], [], item, 2)]
					: acc,
			[]
		),
	]
}

export interface MobileMenus {
	primary?: MenuItem[]
	secondary?: MenuItem[]
}

const MobilePrimaryMenu: React.FC<Pick<PrimaryMenuProps, "menu">> = (props) => {
	const { primaryMobileMenuOpen, togglePrimaryMobileMenuOpen } =
		React.useContext(UIContext)

	const menus = props.menu.reduce<MobileMenus>((acc, menu) => {
		const key = menu.id === 0 ? "secondary" : "primary"
		if (!acc[key]) acc[key] = []

		acc[key].push(menu)

		return acc
	}, {})

	return (
		<>
			{props.menu && props.menu.length > 0 && (
				<Drawer
					isOpened={primaryMobileMenuOpen}
					onClose={togglePrimaryMobileMenuOpen}
					zIndex={Z_INDEX.Drawer}
				>
					<StackNavigation
						initialViewId={String(props.menu[0].id)}
						reset={!primaryMobileMenuOpen}
						views={mapMenu(
							togglePrimaryMobileMenuOpen,
							menus?.primary,
							menus?.secondary?.[0]?.children
						)}
					>
						<Drawer.Header>
							<StackNavigationContext.Consumer>
								{({ history, goBack, currentView }) => (
									<Flex
										flex={1}
										justifyContent="space-between"
										alignItems="center"
									>
										<Flex>
											{history.length > 1 && (
												<button onClick={goBack}>
													<Icon icon="arrow-left" />
												</button>
											)}
										</Flex>
										<Flex>
											{history.length > 1 && (
												<Text mb={0} fontWeight="bold">
													{currentView.title}
												</Text>
											)}
										</Flex>
										<Flex mr={-0.5}>
											<button onClick={togglePrimaryMobileMenuOpen}>
												<Icon icon="close" />
											</button>
										</Flex>
									</Flex>
								)}
							</StackNavigationContext.Consumer>
						</Drawer.Header>
						<Drawer.Content>
							<StackNavigation.Views />
						</Drawer.Content>
					</StackNavigation>
				</Drawer>
			)}
		</>
	)
}

export { MobilePrimaryMenu }
